import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useHttpClient } from '@bonusly/http-client';
import { useCurrentCompany } from '@bonusly/who-am-i';

import { ReviewCycleTransformer, type ReviewCycle, type ReviewCycleApiData } from '../types/review-cycle';

// query keys
export const reviewCycleKeys = {
	all: ['review-cycles'] as const,
	lists: () => [...reviewCycleKeys.all, 'list'] as const,
	list: (filters: Record<string, unknown>) => [...reviewCycleKeys.lists(), filters] as const,
	active: () => [...reviewCycleKeys.lists(), { status: 'active' }] as const,
};

// fetch review cycles (with optional active filter)
export const useReviewCycles = (params?: { status?: 'active' | 'inactive' }) => {
	const http = useHttpClient();
	const company = useCurrentCompany();

	return useQuery({
		queryKey: reviewCycleKeys.list(params || {}),
		queryFn: async (): Promise<ReviewCycle[]> => {
			const queryParams = params?.status ? `?status=${params.status}` : '';
			const response = await http.get<ReviewCycleApiData[]>(`/achieve/review_cycles${queryParams}`);
			return response.data.map((cycle) => ReviewCycleTransformer.fromApiData(cycle));
		},
		enabled: !!company.id,
	});
};

// fetch active review cycle
export const useActiveReviewCycle = () => {
	const http = useHttpClient();
	const company = useCurrentCompany();

	return useQuery({
		queryKey: reviewCycleKeys.active(),
		queryFn: async (): Promise<ReviewCycle | null> => {
			const response = await http.get<ReviewCycleApiData[]>('/achieve/review_cycles?status=active');
			if (response.data.length === 0) return null;
			return ReviewCycleTransformer.fromApiData(response.data[0]);
		},
		enabled: !!company.id,
	});
};

// create or update review cycle
interface CreateOrUpdateReviewCycleParams {
	notificationCadence: string;
	nextReviewDate: Date | null;
	active?: boolean;
}

export const useCreateOrUpdateReviewCycle = () => {
	const queryClient = useQueryClient();
	const http = useHttpClient();

	return useMutation({
		mutationFn: async (params: CreateOrUpdateReviewCycleParams): Promise<ReviewCycle> => {
			const apiData = ReviewCycleTransformer.toApiData({
				notificationCadence: params.notificationCadence,
				nextReviewDate: params.nextReviewDate,
				active: params.active,
			});

			const response = await http.post<ReviewCycleApiData>('/achieve/review_cycles', apiData);
			return ReviewCycleTransformer.fromApiData(response.data);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: reviewCycleKeys.all });
		},
	});
};
